import React, { Children, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { OS } from '@/utils/detectDevice';
import { DEVICE } from '@/constants';
import Animated from './Animated';
// import style from './style.less';

class FlippingPages extends PureComponent {
  static propTypes = {
    children: PropTypes.array,
    className: PropTypes.string,
    onPointerDown: PropTypes.func,
    animationDuration: PropTypes.number,
    direction: PropTypes.string,
    onOverSwipe: PropTypes.func,
    onSelectedChange: PropTypes.func,
    onSwipeStart: PropTypes.func,
    selected: PropTypes.number.isRequired,
    swipeLength: PropTypes.number,
    thresholdSpeed: PropTypes.number,
  }

  static defaultProps = {
    direction: 'horizontal',
    animationDuration: 400,
    onOverSwipe: swipe => swipe / 4,
    onSwipeStart: event => OS.TYPE !== DEVICE.PC || event.isPrimary,
    swipeLength: 400,
    thresholdSpeed: 0.1,
  }

  constructor() {
    super();
    this.state = {
      down: false,
      selected: 0,
    };
  }

  componentDidMount() {
    if (OS.TYPE === DEVICE.PC) {
      window.addEventListener('pointermove', this.handlePointerMove);
      window.addEventListener('pointerup', this.handlePointerUp);
      window.addEventListener('pointercancel', this.handlePointerUp);
    } else {
      window.addEventListener('touchmove', e => this.handlePointerMove(e));
      window.addEventListener('touchstart', e => this.handlePointerDown(e));
      window.addEventListener('touchend', this.handlePointerUp);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.selected !== prevProps.selected) {
      if (this.state.down) {
        setTimeout(() => this.setState({ down: false }), 0);
        this.updateSelectedWhenUp();
      } else {
        setTimeout(() => this.setState({ selected: this.props.selected }), 0);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerUp);
  }

  getTurn() {
    const swipe = this.startX - this.x;
    const turn = swipe / this.props.swipeLength;
    return turn;
  }

  handlePointerDown = (event) => {
    // Determine if the touch event started on a scrollable element
    const isScrollableStart = (evt) => {
      let el = evt.target;
      if (el && el.nodeType === 3) {
        el = el.parentNode;
      }
      while (el && el !== document.body) {
        if (el.classList && el.classList.contains('word-box')) {
          return true;
        }
        const style = window.getComputedStyle(el);
        const overflowY = style.getPropertyValue('overflow-y');
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
          return true;
        }
        el = el.parentElement;
      }
      return false;
    };

    if (isScrollableStart(event)) {
      return;
    }

    let touch = null;
    if (event.touches) {
      touch = {
        clientX: event.touches[0].clientX,
      };
    } else {
      touch = event;
    }
    const { onPointerDown, onSwipeStart } = this.props;
    if (!this.down && this.state.selected === this.props.selected && onSwipeStart(event)) {
      this.setState({ down: true });
      this.id = event.pointerId;
      this.startSelected = this.state.selected;
      this.x = event.clientX || touch.clientX;
      this.startX = this.x;
      this.lastX = this.x;
      this.time = new Date().getTime();
      this.lastTime = this.time;
    }
    if (onPointerDown) {
      onPointerDown(event);
    }
  }

  handlePointerUp = (event) => {
    if (!this.state.down
      || (OS.TYPE === DEVICE.PC && event.pointerId !== this.id)) {
      return;
    }
    this.setState({ down: false });
    this.updateSelectedWhenUp();
  }

  handlePointerMove = (event) => {
    let touch = null;
    if (event.touches) {
      touch = {
        clientX: event.touches[0].clientX,
      };
    } else {
      touch = event;
    }
    if (!this.state.down || (OS.TYPE === DEVICE.PC && event.pointerId !== this.id)) {
      return;
    }
    this.x = event.clientX || touch.clientX;
    this.time = new Date().getTime();
    OS.TYPE === DEVICE.PC && this.updateSelectedWhenDown();
  }

  updateSelectedWhenDown() {
    const turn = this.getTurn();
    let selected = this.props.selected + turn;
    selected = Math.max(selected, this.props.selected - 1);
    selected = Math.min(selected, this.props.selected + 1);
    const childrenCount = Children.count(this.props.children);
    if (selected < 0) {
      selected = -this.props.onOverSwipe(-selected);
    } else if (selected + 1 > childrenCount) {
      selected = this.props.onOverSwipe(selected + 1 - childrenCount) + childrenCount - 1;
    }
    this.setState({ selected });
  }

  updateSelectedWhenUp() {
    this.setState({ selected: this.props.selected });
    const turn = this.getTurn();
    const speed = (this.props.direction === 'horizontal' ? this.lastX - this.x : this.lastY - this.y) / (this.time - this.lastTime);
    let selectedProps = this.props.selected;
    if (!turn) {
      if (Math.abs(speed) >= this.props.thresholdSpeed) {
        selectedProps += Math.sign(speed);
      }
    } else if (Math.abs(turn) < 0.5) {
      if (Math.abs(speed) >= this.props.thresholdSpeed && Math.sign(speed) === Math.sign(turn)) {
        selectedProps += Math.sign(speed);
      }
    } else if (Math.abs(speed) < this.props.thresholdSpeed
    || Math.sign(speed) !== Math.sign(turn)) {
      selectedProps += Math.sign(turn);
    }
    selectedProps = Math.max(selectedProps, 0);
    selectedProps = Math.min(selectedProps, Children.count(this.props.children) - 1);
    const { onSelectedChange } = this.props;
    if (onSelectedChange) {
      onSelectedChange(selectedProps);
    }
  }

  render() {
    const {
      animationDuration,
      className,
      onOverSwipe,
      onPointerDown,
      onSelectedChange,
      onSwipeStart,
      selected,
      swipeLength,
      thresholdSpeed,
      ...otherProps
    } = this.props;
    return (
      <Animated
        animationDuration={this.state.down ? 0 : this.props.animationDuration}
        className={className}
        onPointerDown={this.handlePointerDown}
        selected={this.state.selected}
        willChange={this.state.down}
        {...otherProps}
      />
    );
  }
}

export default FlippingPages;
